import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, switchMap, tap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { CsrfService } from './csrf.service';

export interface WaitlistSignup {
  name: string;
  email: string;
  phone?: string;
  organization?: string;
  consent: boolean;
  /** Honeypot -- always blank for a real visitor. */
  website?: string;
}

export type WaitlistState = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Registers a visitor for the launch announcement while the partner
 * workspace is gated.
 *
 * The endpoint is public but the API is stateful, so the XSRF cookie has to
 * be in place before the POST or it comes back 419.
 */
@Injectable({ providedIn: 'root' })
export class WaitlistService {
  private readonly http = inject(HttpClient);
  private readonly csrf = inject(CsrfService);

  readonly state = signal<WaitlistState>('idle');
  readonly error = signal('');

  join(signup: WaitlistSignup): Observable<void> {
    this.state.set('submitting');
    this.error.set('');

    return this.csrf.ensure().pipe(
      switchMap(() =>
        this.http.post<{ success: boolean; message: string }>(
          `${environment.apiBaseUrl}/core/public/waitlist`,
          { ...signup, source: 'partner_gate' },
        ),
      ),
      tap(() => this.state.set('success')),
      map(() => undefined),
      catchError((err: HttpErrorResponse) => {
        this.error.set(
          err.error?.message || 'We could not add you to the list. Please try again in a moment.',
        );
        this.state.set('error');
        return throwError(() => err);
      }),
    );
  }

  /** Lets the form return to an editable state after a failed attempt. */
  reset(): void {
    this.state.set('idle');
    this.error.set('');
  }
}
